import { useEffect, useState } from 'react';
import { toast } from '../../utils/toast';
import { useParams, Navigate } from 'react-router-dom';
import { useMaintenanceApi } from '../../services/api/maintenanceApi';
import { GenericItem, MaintenanceDto } from '../../components/types/generic.types';
import GenericDataTable from '../../components/common/GenericDataTable/GenericDataTable';
import MaintenanceModal from '../../components/common/MaintenanceModal/MaintenanceModal';
import PageLayout from "../../components/layout/PageLayout";
import { MODULE_CONFIGS } from './config/maintenanceConfig';
import Swal from 'sweetalert2';

const MaintenanceModule = () => {
  const { submodule } = useParams<{ submodule: string }>();
  const [items, setItems] = useState<GenericItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GenericItem | null>(null);
  
  const { isLoading, getAll, create, update, remove } = useMaintenanceApi(submodule || '');

  const loadItems = async () => {
    if (!submodule) return;   
    const data = await getAll();    
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    if (submodule) {
      loadItems();
    }
  }, [submodule]);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: GenericItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: "¿Desea eliminar este registro?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#7451f8',
        cancelButtonColor: '#e74c3c',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        await remove(id);
        await loadItems();
        toast.success('El registro ha sido eliminado exitosamente');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('No se pudo eliminar el registro.');
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    try {
      const item = items.find(item => item.Id === id);
      if (item) {
        const updateData: MaintenanceDto = {
          Nombre: item.Nombre,
          Activo: active
        };
        await update(id, updateData);
        await loadItems();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('No se pudo actualizar el estado.');
    }
  };

  const handleSuccess = (message: string) => {
    toast.success('Éxito');
  };

  const handleModalSubmit = async (data: MaintenanceDto) => {
    try {
      setIsModalOpen(false);
  
      if (editingItem) {
        const result = await Swal.fire({
          title: '¿Está seguro?',
          text: 'Se actualizará el registro',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, actualizar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#7451f8',
          cancelButtonColor: '#e74c3c',
        });
  
        if (result.isConfirmed) {
          await update(editingItem.Id, data);
          handleSuccess('Registro actualizado exitosamente');
          await loadItems();
        } else {
          setIsModalOpen(true);
        }
      } else {
        await create(data);
        handleSuccess('Registro creado exitosamente');
        await loadItems();
      }
    } catch (error) {
      console.error('Error saving item:', error);
      setIsModalOpen(true);
      toast.error('No se pudo guardar el registro.');
    }
  };

  if (!submodule || !MODULE_CONFIGS[submodule]) {
    return <Navigate to="/mantenimiento" replace />;
  }

  const config = MODULE_CONFIGS[submodule];

  return (
    <PageLayout 
      icon={config.icon.type} 
      title={config.title}
    >
      <div style={{ padding: '24px' }}>
        <GenericDataTable
          items={items}
          title={config.title}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onAdd={handleAdd}
          isLoading={isLoading}
        />
        <MaintenanceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
          title={editingItem ? 'Editar Registro' : 'Nuevo Registro'}
          initialData={editingItem ? {
            Nombre: editingItem.Nombre,
            Activo: editingItem.Activo
          } : {
            Nombre: '',
            Activo: true
          }}
          isLoading={isLoading}
          isEdit={!!editingItem}
        />
      </div>
    </PageLayout>
  );
};

export default MaintenanceModule;